import type { ExtensionWorkerStatus } from "../../api/extension-worker-status";
import { apiGet, apiPost, buildQuery } from "./http";

export interface SisterBrowserQueueEnqueueResult {
  scanned: number;
  enqueued: number;
  skipped: number;
  /** Stessa persona già in coda da un altro necrologio (evita visure doppie). */
  skippedDuplicatePerson: number;
  /** Ricerche in attesa tolte con `clearQueued`. */
  clearedQueued: number;
  items: Array<{ id: number; necrologioId: number | null; state: string }>;
  stats: {
    totalNecrologi: number;
    eligibleWithAnno: number;
    ineligibleNoAnno: number;
    alreadyInQueue: number;
  };
}

export function enqueueSisterBrowserNecrologi(input: {
  limit?: number;
  provincia?: string | null;
  /** Selezione esplicita dall'anteprima: accoda solo questi necrologi. */
  necrologioIds?: number[];
  /** Svuota prima la coda in attesa: l'estensione cerca solo la selezione. */
  clearQueued?: boolean;
}): Promise<SisterBrowserQueueEnqueueResult> {
  return apiPost<SisterBrowserQueueEnqueueResult>("/api/sister3/browser-queue/enqueue-necrologi", input);
}

export interface SisterEnqueuePreviewItem {
  necrologioId: number;
  nome: string;
  cognome: string | null;
  nomeProprio: string | null;
  annoNascita: number | null;
  dataDecesso: string | null;
  provincia: string | null;
  comune: string | null;
  casaVuota: boolean;
  vedovo: boolean;
  selectable: boolean;
  duplicatePerson: boolean;
  warning: string | null;
}

export interface SisterEnqueuePreviewResult {
  items: SisterEnqueuePreviewItem[];
  /** Ricerche già in attesa: l'estensione le esegue prima delle nuove. */
  queuedWaiting: number;
  stats: SisterBrowserQueueEnqueueResult["stats"];
}

export function previewSisterBrowserNecrologi(input: {
  limit?: number;
  provincia?: string | null;
}): Promise<SisterEnqueuePreviewResult> {
  return apiPost<SisterEnqueuePreviewResult>("/api/sister3/browser-queue/preview-necrologi", input);
}

export interface SisterQueueTask {
  id: number;
  state: string;
  necrologioId: number | null;
  attempts: number;
  error: string | null;
  /** Inizio del claim corrente (ISO) — serve al pannello per rilevare gli
   * zombie: claimed/running con lock stale e tentativi esauriti. */
  lockedAt: string | null;
  createdAt: string;
  updatedAt: string;
  recovery: {
    idRichiesta: string | null;
    needsRecovery: boolean;
    alreadyRequested: boolean;
  };
  payload: {
    rawName?: string;
    cognome?: string;
    nome?: string;
    annoNascita?: number | null;
    provincia?: string | null;
    comune?: string | null;
  };
}

export type SisterQueueFilter = "active" | "queued" | "in_progress" | "needs_review" | "failed" | "recovery" | "done" | "skipped";

export interface SisterQueueResponse {
  byState: Array<{ state: string; count: number }>;
  items: SisterQueueTask[];
  retryableCount: number;
  recoveryCount: number;
  total: number;
  offset: number;
  limit: number;
  /** Whether the organization's extensions are running the queue (null on old servers). */
  worker?: ExtensionWorkerStatus | null;
}

export function listSisterBrowserQueue(input: number | {
  limit?: number;
  offset?: number;
  filter?: SisterQueueFilter;
} = 50): Promise<SisterQueueResponse> {
  const query = typeof input === "number" ? { limit: input } : input;
  return apiGet(`/api/sister3/browser-worker/queue${buildQuery(query)}`);
}

export function cancelSisterBrowserTasks(ids: number[]): Promise<{ removed: number; archived: number }> {
  return apiPost("/api/sister3/browser-queue/cancel", { ids });
}

export function clearQueuedSisterBrowserTasks(): Promise<{ removed: number; kept: number }> {
  return apiPost("/api/sister3/browser-queue/clear-queued", {});
}

export function prioritizeSisterBrowserTask(id: number): Promise<{ ok: boolean }> {
  return apiPost(`/api/sister3/browser-queue/task/${id}/prioritize`, {});
}

export function requeueFailedSisterBrowserTasks(): Promise<{ requeued: number }> {
  return apiPost<{ requeued: number }>("/api/sister3/browser-queue/requeue-failed", {});
}

// Spostato in contract.ts (single source of truth REST), ri-esportato per
// compatibilità con gli import esistenti via src/api.ts.
export type { Omonimo } from "../../api/contract";
